import { computed, nextTick, onScopeDispose, onMounted, ref, toValue, unref, watch } from 'vue';
import { useQuery, useQueryClient } from '@tanstack/vue-query';

import { useEventHub } from '@satellite/composables/useEventHub';
import appointmentService from '@satellite/services/appointment-service';

import { DEFAULT_INVALIDATE_OPTIONS, queryKeys } from './queryKeys';
import {
  EntityPresenterV2Mode,
  isEntityPresenterV2QueryEnabled,
  isLazyLiveMode,
  isStaticMode,
  subscribesToEventHub
} from './presenterV2EntityMode';

import type { ComputedRef, Ref } from 'vue';
import type { Maybe } from '@nova/core';
import type { PresenterPlaceholderData } from './presenterV2Placeholder';
import type { AppointmentDetails } from './appointmentPresenterV2Shared';

/**
 * Shared CRUD `join` for the appointment GET. Field-limited so payloads stay lean while
 * covering common presenter needs: assetVisit summary, dock/loadType labels, warehouse
 * (timezone, settings, access fields — used by the edit-appointment shell), and
 * recurringParent (recurring-pattern header).
 */
const APPOINTMENT_QUERY_JOINS = [
  'assetVisit||id,visitType,companyId',
  'dock||id,name,warehouseId',
  'loadType||id,name',
  'dock.warehouse||id,orgId,timezone,settings,name,email,phone,allowCarrierScheduling',
  'recurringParent||id,start,recurringPattern'
];

export interface UseAppointmentFetcherV2Options {
  /**
   * Appointment ID to fetch. Can be a ref for reactivity.
   * Optional if initialAppointmentData (with id) is provided.
   */
  appointmentId?: Ref<Maybe<string>> | ComputedRef<Maybe<string>>;

  /**
   * Optional initial appointment data used as initialData for the query.
   */
  initialAppointmentData?:
    | Ref<Maybe<AppointmentDetails>>
    | ComputedRef<Maybe<AppointmentDetails>>
    | AppointmentDetails
    | null;

  /**
   * Optional incomplete appointment data shown as placeholderData while the query fetches.
   */
  placeholderAppointmentData?:
    | Ref<Maybe<PresenterPlaceholderData<AppointmentDetails>>>
    | ComputedRef<Maybe<PresenterPlaceholderData<AppointmentDetails>>>
    | PresenterPlaceholderData<AppointmentDetails>
    | null;

  /**
   * When true, the query runs without viewport gating.
   * Default: false
   */
  headless?: boolean;

  /**
   * - `live`: fetches when enabled and subscribes to EventHub updates (default)
   * - `lazy-live`: no fetch until first matching EventHub event, refresh(), or invalidate()
   * - `static`: no fetch/subscriptions; returns staticAppointmentData only
   */
  mode?: EntityPresenterV2Mode;

  /**
   * Data source for `mode: 'static'`.
   */
  staticAppointmentData?:
    | Ref<Maybe<AppointmentDetails>>
    | ComputedRef<Maybe<AppointmentDetails>>
    | AppointmentDetails
    | null;

  staleTime?: number;

  onUpdateReceived?: (appointment: AppointmentDetails) => void;
}

export function useAppointmentFetcherV2(options: UseAppointmentFetcherV2Options) {
  const {
    appointmentId,
    initialAppointmentData,
    placeholderAppointmentData,
    headless = false,
    mode = EntityPresenterV2Mode.Live,
    staticAppointmentData,
    staleTime = Infinity,
    onUpdateReceived
  } = options;

  const eventHub = useEventHub();
  const queryClient = useQueryClient();

  const effectiveAppointmentId = computed((): string | null => {
    const explicitId = unref(appointmentId);
    const fromObject = toValue(initialAppointmentData)?.id ?? null;

    if (explicitId) {
      if (fromObject && fromObject !== explicitId) {
        console.warn(
          '[useAppointmentFetcherV2] appointmentId mismatch:',
          `explicit appointmentId (${explicitId}) does not match appointment.id (${fromObject}). Using explicit appointmentId.`
        );
      }
      return explicitId;
    }
    return fromObject;
  });

  const initialAppointment = computed(() => toValue(initialAppointmentData) ?? undefined);
  const placeholderAppointment = computed(() => toValue(placeholderAppointmentData) ?? undefined);
  const staticAppointment = computed(() => toValue(staticAppointmentData) ?? undefined);

  if (
    isStaticMode(mode) &&
    (toValue(initialAppointmentData) != null || toValue(placeholderAppointmentData) != null)
  ) {
    console.warn(
      '[useAppointmentFetcherV2] mode=static ignores initialAppointmentData/placeholderAppointmentData. Use staticAppointmentData.'
    );
  }

  if (isLazyLiveMode(mode) && toValue(initialAppointmentData) != null) {
    console.warn(
      '[useAppointmentFetcherV2] mode=lazy-live does not use initialAppointmentData for the query cache; use placeholderAppointmentData for partial UI.'
    );
  }

  const lazyLiveUnlocked = ref(!isLazyLiveMode(mode));
  const isInViewport = ref(headless);
  const pendingInvalidation = ref(false);

  const wakeLazyLive = () => {
    if (isLazyLiveMode(mode) && !lazyLiveUnlocked.value) {
      lazyLiveUnlocked.value = true;
    }
  };

  const appointmentQuery = useQuery({
    queryKey: computed(() =>
      effectiveAppointmentId.value ? queryKeys.appointment(effectiveAppointmentId.value) : []
    ),
    queryFn: async ({ signal }) => {
      if (!effectiveAppointmentId.value) {
        return null;
      }

      const response = await appointmentService.getAppointmentByIdV2(effectiveAppointmentId.value, {
        requestConfig: {
          signal,
          params: {
            join: APPOINTMENT_QUERY_JOINS
          }
        }
      });

      return response as AppointmentDetails;
    },
    enabled: computed(() =>
      isEntityPresenterV2QueryEnabled({
        mode,
        hasEffectiveId: Boolean(effectiveAppointmentId.value),
        isInViewport: isInViewport.value,
        lazyLiveUnlocked: lazyLiveUnlocked.value
      })
    ),
    initialData: computed(() =>
      isStaticMode(mode) || isLazyLiveMode(mode) ? undefined : initialAppointment.value
    ),
    placeholderData: computed(() => {
      if (isStaticMode(mode)) {
        return undefined;
      }
      return placeholderAppointment.value as AppointmentDetails | undefined;
    }),
    staleTime,
    refetchOnWindowFocus: false
  });

  const data = computed((): AppointmentDetails | null => {
    if (isStaticMode(mode)) {
      if (!staticAppointment.value) {
        console.warn('[useAppointmentFetcherV2] mode=static requires staticAppointmentData.');
        return null;
      }
      return staticAppointment.value;
    }
    return appointmentQuery.data.value || null;
  });

  const isLoading = computed(() => (isStaticMode(mode) ? false : appointmentQuery.isLoading.value));
  const isFetching = computed(() =>
    isStaticMode(mode) ? false : appointmentQuery.isFetching.value
  );
  const error = computed(() => (isStaticMode(mode) ? null : appointmentQuery.error.value));

  const invalidate = () => {
    if (isStaticMode(mode)) {
      return;
    }

    wakeLazyLive();

    if (effectiveAppointmentId.value) {
      queryClient.invalidateQueries(
        {
          queryKey: queryKeys.appointment(effectiveAppointmentId.value)
        },
        DEFAULT_INVALIDATE_OPTIONS
      );
    }
  };

  const refresh = async () => {
    if (isStaticMode(mode)) {
      return;
    }
    wakeLazyLive();
    await nextTick();
    await appointmentQuery.refetch();
  };

  const deferOrInvalidate = () => {
    wakeLazyLive();

    if (!isInViewport.value) {
      pendingInvalidation.value = true;
      return;
    }

    invalidate();
  };

  const handleIntersect = (entry?: { isIntersecting?: boolean } | boolean) => {
    if (headless) {
      return;
    }

    const isIntersecting = typeof entry === 'boolean' ? entry : entry?.isIntersecting ?? true;
    isInViewport.value = isIntersecting;

    if (isIntersecting && pendingInvalidation.value) {
      pendingInvalidation.value = false;
      invalidate();
    }
  };

  const handleAppointmentEvent = (event: { id?: string }) => {
    if (!event?.id || event.id !== effectiveAppointmentId.value) {
      return;
    }

    deferOrInvalidate();
  };

  watch(data, newAppointment => {
    if (onUpdateReceived && newAppointment) {
      onUpdateReceived(newAppointment);
    }
  });

  watch(effectiveAppointmentId, (newId, oldId) => {
    if (newId !== oldId) {
      pendingInvalidation.value = false;
      lazyLiveUnlocked.value = !isLazyLiveMode(mode);
    }
  });

  onMounted(() => {
    if (subscribesToEventHub(mode)) {
      eventHub.on('update-Appointment', handleAppointmentEvent);
    }
  });

  onScopeDispose(() => {
    if (subscribesToEventHub(mode)) {
      eventHub.off('update-Appointment', handleAppointmentEvent);
    }
  });

  return {
    data,
    isLoading,
    isFetching,
    error,
    refresh,
    invalidate,
    onIntersect: handleIntersect,
    query: appointmentQuery
  };
}
